import XUILoader from '@xero/xui/react/loader';
import * as React from 'react';
import { translate } from '../../helpers/translate';
import { IInstallation, IIntegrationEvent } from '../../models';
import { integrationEventNames } from '../../static/constants';
import lang from '../../static/lang/en-NZ';
import './ActivityTable.css';

export interface ITableProps {
    installation: IInstallation;
    integrationEvents?: IIntegrationEvent[];
}

const groupEventsByDateAndName = (events: IIntegrationEvent[]) => {
    if (events.length === 0) {
        return new Map<string, IIntegrationEvent[]>();
    }
    let groupKey = 0;
    let previousEventName = events[0].eventName;
    return events.reduce((acc: Map<string, IIntegrationEvent[]>, event: IIntegrationEvent) => {
        if (previousEventName !== event.eventName) {
            groupKey++;
        }
        previousEventName = event.eventName;

        const date = new Date(event.eventDate).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
        const key = `${date}|${groupKey}`;

        acc[key] = acc[key] || [];
        acc[key].unshift({
            ...event,
            displayDate: date,
        } as IIntegrationEvent);

        return acc;
    }, new Map<string, IIntegrationEvent[]>());
};

export default class Table extends React.Component<ITableProps> {
    public render() {
        const { integrationEvents } = this.props;

        if (!integrationEvents) {
            return <XUILoader ariaLabel='Loading' />;
        }

        const groupedEvents = groupEventsByDateAndName(integrationEvents);

        return (
            <React.Fragment>
                <div className='xui-heading-large xui-padding-bottom'>{lang.ACTIVITY_TABLE.TITLE}</div>
                <table className='xui-readonlytable xui-table-hasheader'>
                    <tbody className='xui-readonlytablebody'>
                        {Object.keys(groupedEvents).map((key) => {
                            return this.renderRow(key, groupedEvents[key]);
                        })}
                    </tbody>
                </table>
            </React.Fragment>
        );
    }

    private renderRow = (key: string, events: IIntegrationEvent[]) => {
        const row = this.renderRowDetails(events);
        return row ? (
            <tr key={key} className='xui-readonlytablerow'>
                <td className={'xui-readonlytablecell separater activity-table-row-layout'}>{row}</td>
                <td className={'xui-readonlytablecell separater xui-text-align-right activity-table-row-layout'}>
                    {events[0].displayDate}
                </td>
            </tr>
        ) : null;
    };

    private renderRowDetails(events: IIntegrationEvent[]) {
        const vars = { qty: events.length };
        const displayName = this.props.installation.displayName;
        const isSingularEvent = events.length === 1;

        let primaryText = '';
        let secondaryText = '';

        switch (events[0].eventName) {
            case integrationEventNames.INTEGRATION_CONNECTED:
                primaryText = lang.ACTIVITY_TABLE.CONNECTION_CREATED;
                secondaryText = translate(lang.ACTIVITY_TABLE.BY_USERNAME, { username: events[0].userName });
                break;
            case integrationEventNames.INTEGRATION_UPDATED:
                primaryText = lang.ACTIVITY_TABLE.VERSION_UPDATED;
                secondaryText = translate(lang.ACTIVITY_TABLE.BY_USERNAME, { username: events[0].userName });
                break;
            case integrationEventNames.CONTACT_SENT:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.A_CONTACT
                    : translate(lang.ACTIVITY_TABLE.QTY_CONTACTS, vars);
                secondaryText = translate(lang.ACTIVITY_TABLE.SENT_TO, { integrationName: displayName });
                break;
            case integrationEventNames.ERROR_LIST_NOT_FOUND:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.LIST_NOT_FOUND;
                break;
            case integrationEventNames.ERROR_REQUIRED_MERGE_FIELDS:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.REQUIRED_MERGE_FIELDS;
                break;
            case integrationEventNames.ERROR_CONNECTION:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = translate(lang.ACTIVITY_TABLE.CONNECTION_ERROR_WITH_INSTALLATION, {
                    installation: displayName,
                });
                break;
            case integrationEventNames.ORDER_PROCESSED:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.AN_ORDER
                    : translate(lang.ACTIVITY_TABLE.QTY_ORDERS, vars);
                secondaryText = translate(lang.ACTIVITY_TABLE.PROCESSED_FOR, { integrationName: displayName });
                break;
            case integrationEventNames.XERO_CONTACT_CREATED:
                primaryText = lang.ACTIVITY_TABLE.CONTACT_CREATED;
                secondaryText = translate(lang.ACTIVITY_TABLE.BY_USERNAME, { username: displayName });
                break;
            case integrationEventNames.XERO_INVOICE_CREATED:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.AN_INVOICE_CREATED
                    : translate(lang.ACTIVITY_TABLE.QTY_INVOICES_CREATED, vars);
                secondaryText = translate(lang.ACTIVITY_TABLE.BY_USERNAME, { username: displayName });
                break;
            case integrationEventNames.XERO_BILL_CREATED:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.A_BILL_CREATED
                    : translate(lang.ACTIVITY_TABLE.QTY_BILLS_CREATED, vars);
                secondaryText = translate(lang.ACTIVITY_TABLE.BY_USERNAME, { username: displayName });
                break;
            case integrationEventNames.XERO_PAYMENT_CREATED:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.AN_INVOICE_PAYMENT
                    : translate(lang.ACTIVITY_TABLE.QTY_INVOICE_PAYMENTS, vars);
                secondaryText = `${lang.ACTIVITY_TABLE.MADE_FROM_CLEARING_ACCOUNT}`;
                break;
            case integrationEventNames.SHOPIFY_PAYOUT_MADE:
            case integrationEventNames.BANK_TRANSFER_FROM_CLEARING_ACCOUNT:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.A_TRANSFER
                    : translate(lang.ACTIVITY_TABLE.QTY_TRANSFERS, vars);
                secondaryText = lang.ACTIVITY_TABLE.CLEARING_TO_BANK;
                break;
            case integrationEventNames.BANK_TRANSFER_TO_CLEARING_ACCOUNT:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.A_TRANSFER
                    : translate(lang.ACTIVITY_TABLE.QTY_TRANSFERS, vars);
                secondaryText = lang.ACTIVITY_TABLE.BANK_TO_CLEARING;
                break;
            case integrationEventNames.BANK_ACCOUNT_MAPPED:
                primaryText = lang.ACTIVITY_TABLE.BANK_ACCOUNT;
                secondaryText = lang.ACTIVITY_TABLE.MAPPED;
                break;
            case integrationEventNames.BANK_ACCOUNT_UPDATED:
                primaryText = lang.ACTIVITY_TABLE.BANK_ACCOUNT;
                secondaryText = lang.ACTIVITY_TABLE.MAPPINGS_UPDATED;
                break;
            case integrationEventNames.BANK_ACCOUNT_NOT_FOUND:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.BANK_ACCOUNT_LINKED_DELETED;
                break;
            case integrationEventNames.BANK_ACCOUNT_NOT_FOUND_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.BANK_ACCOUNTS_VALID;
                break;
            case integrationEventNames.CLEARING_ACCOUNT_CREATED:
                primaryText = lang.ACTIVITY_TABLE.CLEARING_ACCOUNT;
                secondaryText = lang.ACTIVITY_TABLE.CREATED_BY_XERO;
                break;
            case integrationEventNames.CLEARING_ACCOUNT_MAPPED:
                primaryText = lang.ACTIVITY_TABLE.CLEARING_ACCOUNT;
                secondaryText = lang.ACTIVITY_TABLE.MAPPED;
                break;
            case integrationEventNames.CLEARING_ACCOUNT_UPDATED:
                primaryText = lang.ACTIVITY_TABLE.CLEARING_ACCOUNT;
                secondaryText = lang.ACTIVITY_TABLE.MAPPINGS_UPDATED;
                break;
            case integrationEventNames.CLEARING_ACCOUNT_NOT_FOUND:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.CLEARING_ACCOUNT_LINKED_DELETED;
                break;
            case integrationEventNames.CLEARING_ACCOUNT_NOT_FOUND_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.CLEARING_ACCOUNTS_VALID;
                break;
            case integrationEventNames.CHART_OF_ACCOUNT_MAPPED:
                primaryText = lang.ACTIVITY_TABLE.CHART_OF_ACCOUNTS;
                secondaryText = lang.ACTIVITY_TABLE.MAPPED;
                break;
            case integrationEventNames.CHART_OF_ACCOUNT_CREATED:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.CHART_OF_ACCOUNT
                    : lang.ACTIVITY_TABLE.CHART_OF_ACCOUNTS;
                secondaryText = lang.ACTIVITY_TABLE.CREATED_BY_XERO;
                break;
            case integrationEventNames.CHART_OF_ACCOUNT_UPDATED:
                primaryText = lang.ACTIVITY_TABLE.CHART_OF_ACCOUNT;
                secondaryText = lang.ACTIVITY_TABLE.MAPPINGS_UPDATED;
                break;
            case integrationEventNames.CHART_OF_ACCOUNT_NOT_FOUND:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.COA_LINKED_DELETED;
                break;
            case integrationEventNames.CHART_OF_ACCOUNT_NOT_FOUND_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.COAS_VALID;
                break;
            case integrationEventNames.TAX_RATE_MAPPED:
                primaryText = lang.ACTIVITY_TABLE.TAX_RATES;
                secondaryText = lang.ACTIVITY_TABLE.MAPPED;
                break;
            case integrationEventNames.TAX_RATE_UPDATED:
                primaryText = lang.ACTIVITY_TABLE.TAX_RATE;
                secondaryText = lang.ACTIVITY_TABLE.MAPPINGS_UPDATED;
                break;
            case integrationEventNames.TAX_RATE_NOT_FOUND:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.TAX_RATE_LINKED_DELETED;
                break;
            case integrationEventNames.TAX_RATE_INCORRECT:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.TAX_RATE_LINKED_INCORRECT;
                break;
            case integrationEventNames.TAX_RATE_INCORRECT_RESOLVED:
            case integrationEventNames.TAX_RATE_NOT_FOUND_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.TAX_RATES_VALID;
                break;
            case integrationEventNames.MISSING_TRANSACTION_TYPE_MAPPING:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.MISSING_TRANSACTION_TYPE_MAPPING;
                break;
            case integrationEventNames.MISSING_TRANSACTION_TYPE_MAPPING_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.MISSING_TRANSACTION_TYPES_MAPPED;
                break;
            case integrationEventNames.INVOICE_CONTACT_CREATED:
                primaryText = isSingularEvent
                    ? lang.ACTIVITY_TABLE.CONTACT_CREATED
                    : translate(lang.ACTIVITY_TABLE.QTY_CONTACTS_CREATED, vars);
                secondaryText = translate(lang.ACTIVITY_TABLE.BY_USERNAME, { username: lang.ACTIVITY_TABLE.XERO });
                break;
            case integrationEventNames.CONTACT_NOT_FOUND:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.CONTACT_DELETED;
                break;
            case integrationEventNames.CONTACT_NOT_FOUND_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.CONTACT_VALID;
                break;
            case integrationEventNames.INVOICE_LIMIT_REACHED:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.INVOICE_LIMIT_REACHED;
                break;
            case integrationEventNames.INVOICE_LIMIT_REACHED_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.INVOICE_LIMIT_RESET;
                break;
            case integrationEventNames.FREE_TRIAL_STARTED:
                primaryText = lang.ACTIVITY_TABLE.X_DAY_FREE_TRIAL;
                secondaryText = lang.ACTIVITY_TABLE.STARTED;
                break;
            case integrationEventNames.FREE_TRIAL_EXPIRED:
                primaryText = lang.ACTIVITY_TABLE.X_DAY_FREE_TRIAL;
                secondaryText = lang.ACTIVITY_TABLE.EXPIRED;
                break;
            case integrationEventNames.FREE_TRIAL_EXPIRED_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.SUBSCRIPTION_STARTED;
                break;
            case integrationEventNames.TRANSACTION_IMPORT_RESUMED:
                primaryText = lang.ACTIVITY_TABLE.TRANSACTION_IMPORT_RESUMED;
                secondaryText = lang.ACTIVITY_TABLE.IMPORTING_MISSING_TRANSACTIONS;
                break;
            case integrationEventNames.INSUFFICIENT_USER_PERMISSIONS:
                primaryText = lang.ACTIVITY_TABLE.ERROR;
                secondaryText = lang.ACTIVITY_TABLE.INSUFFICIENT_USER_PERMISSIONS;
                break;
            case integrationEventNames.INSUFFICIENT_USER_PERMISSIONS_RESOLVED:
                primaryText = lang.ACTIVITY_TABLE.RESOLVED;
                secondaryText = lang.ACTIVITY_TABLE.VALID_USER_REASSIGNED;
                break;
            default:
                return null;
        }

        return (
            <div>
                <span className='xui-text-emphasis'>{primaryText}</span> {secondaryText}
            </div>
        );
    }
}
